'use client'

import type { ReactNode } from 'react'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { canSee, type PageId } from '@/lib/access'
import { labelForPage } from '@/lib/navigation'
import { usePersona } from '@/lib/persona'

/**
 * The page is outside the persona's role (plans/03 A5.6). Offers the switch
 * to Supervity support as a button instead of switching silently.
 */
export function NoAccess({ pageLabel }: { pageLabel: string }) {
  const { current, setPersona } = usePersona()
  return (
    <section
      aria-labelledby='no-access-title'
      className='mx-auto mt-10 flex max-w-lg flex-col items-center gap-4 rounded-2xl border border-hairline bg-card p-8 text-center shadow-soft'
    >
      <span className='flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground'>
        <Icons.lock className='h-6 w-6' strokeWidth={1.5} aria-hidden='true' />
      </span>
      <h1 id='no-access-title' className='font-display text-xl font-semibold text-foreground'>
        {pageLabel} is not in the {current.short} role.
      </h1>
      <p className='text-sm text-muted-foreground'>
        Each role sees the pages it works with. Supervity support can open every page.
      </p>
      <Button variant='gradient' onClick={() => setPersona('supervity-support')}>View as Supervity support</Button>
    </section>
  )
}

/**
 * Wraps a page's content and renders `NoAccess` when the persona may not open
 * it (plans/00 5.3 matrix). The backend enforces the same matrix on its `dr`
 * endpoints, so this is never the only guard.
 */
export function RequirePage({ id, children }: { id: PageId; children: ReactNode }) {
  const { persona, hydrated } = usePersona()
  if (!hydrated) {
    return (
      <div className='space-y-4' aria-busy='true'>
        <Skeleton className='h-12 w-64' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }
  if (!canSee(persona, id)) return <NoAccess pageLabel={labelForPage(id)} />
  return <>{children}</>
}
